import { useCallback } from "react";

import { toastLocalizedOperationCatcher } from "./alerts";
import { useOverlayControls } from "./routing/hooks/use-overlay-controls";

import { serverCreateBreakouts, serverEndBreakouts } from "api/api-server-actions";
import { SecondaryOverlay } from "components/constants";
import { BreakoutGroupKind } from "components/server-types";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectActiveClassSessionId } from "store/selectors";

export const MIN_GROUP_COUNT = 2;
export const MAX_GROUP_COUNT = 30;

// In minutes
export const MAX_BREAKOUT_DURATION = 120;
export const DEFAULT_BREAKOUT_DURATION = 10;
export const DEFAULT_BREAKOUT_ADD_MORE_TIME_DURATION = 5;
export const MIN_BREAKOUT_DURATION = 1;

export const DECREMENT_STEP = 1;
export const INCREMENT_STEP = 1;

export const useBreakouts = () => {
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const isMdOrLarger = useIsMdOrLarger();
  const { isSelected, togglePanel } = useOverlayControls(SecondaryOverlay.BreakoutSettings);
  const { canAuthorSection } = useCapabilities();

  const onEndBreakouts = useCallback(() => {
    // Only instructors can end breakout sessions, but still multiple users
    // could be calling serverEndBreakouts at the same time; Forum V classes
    // don't have the concept of a "leader user" yet.
    if (canAuthorSection && !!classSessionId) {
      serverEndBreakouts(classSessionId)
        .then(() => {
          if (isSelected) {
            togglePanel();
          }
        })
        .catch(toastLocalizedOperationCatcher("failed_to_end_breakouts"));
    }
  }, [canAuthorSection, classSessionId, isSelected, togglePanel]);

  const onStartBreakouts = useCallback(
    (
      breakoutGroupType: BreakoutGroupKind,
      breakoutDuration: number,
      groupPageId: string | null,
      groupCount: number,
      shouldSyncBreakoutsWithPageGroups: boolean,
    ) => {
      if (classSessionId) {
        serverCreateBreakouts(
          classSessionId,
          breakoutGroupType,
          breakoutDuration,
          groupPageId,
          groupCount,
          shouldSyncBreakoutsWithPageGroups,
        )
          .then(() => {
            if (!isSelected && isMdOrLarger) {
              togglePanel();
            }
          })
          .catch(toastLocalizedOperationCatcher("failed_to_create_breakouts"));
      }
    },
    [classSessionId, isSelected, togglePanel, isMdOrLarger],
  );

  return { onEndBreakouts, onStartBreakouts };
};
